<template>
  <div class="page-card">
    <div class="page-header">
      <h2 class="page-title">私聊管理</h2>
      <div v-if="noticeTitle" class="notice-filter-tip">
        当前通告：{{ noticeTitle }}
        <el-button type="text" @click="clearNoticeFilter">查看全部</el-button>
      </div>
    </div>

    <div class="toolbar">
      <div class="toolbar-left">
        <el-input
          v-model="query.keyword"
          clearable
          placeholder="搜索商家/艺人/内容"
          style="width: 280px"
          @keyup.enter.native="fetchData"
        />
        <el-select v-model="query.banned" clearable placeholder="封禁状态" style="width: 140px">
          <el-option :value="true" label="已封禁" />
          <el-option :value="false" label="未封禁" />
        </el-select>
        <el-button type="primary" @click="fetchData">查询</el-button>
      </div>
    </div>

    <el-table :data="list" border>
      <el-table-column label="商家" min-width="180">
        <template slot-scope="{ row }">{{ displayMerchantName(row) }}</template>
      </el-table-column>
      <el-table-column prop="artistName" label="艺人" min-width="180" />
      <el-table-column prop="bizTypeText" label="来源类型" width="120" />
      <el-table-column prop="lastMessage" label="最后消息" min-width="260" show-overflow-tooltip />
      <el-table-column prop="lastMessageAt" label="最后时间" width="180" />
      <el-table-column label="未读" width="90" align="center">
        <template slot-scope="{ row }">
          <span v-if="Number(row.unreadCount) > 0" class="unread-badge">{{ unreadBadgeValue(row.unreadCount) }}</span>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="170">
        <template slot-scope="{ row }">
          <el-tag :type="row.banned ? 'danger' : 'success'">
            {{ row.banned ? (row.bannedRoleText || '已封禁') : '正常' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="340" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" @click="openDetail(row)">查看</el-button>
          <template v-if="!row.banned">
            <el-button v-if="!hideMerchantBan(row)" type="text" @click="handleBan(row, 1)">封商家</el-button>
            <el-button type="text" @click="handleBan(row, 2)">封艺人</el-button>
            <el-button v-if="!hideMerchantBan(row)" type="text" @click="handleBan(row, 3)">封双方</el-button>
          </template>
          <el-button v-else type="text" @click="handleUnban(row)">解封</el-button>
          <el-button type="text" @click="openSend(row)">平台发消息</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-drawer :visible.sync="detailVisible" title="私聊详情" size="48%">
      <div v-if="detail.session" class="detail-wrap">
        <div class="detail-head">
          <div>{{ displayMerchantName(detail.session) }} / {{ detail.session.artistName || '-' }}</div>
          <div>{{ detail.session.bizTypeText || '-' }}</div>
        </div>
        <div v-if="detail.session.banned" class="detail-ban">
          <el-tag type="danger">{{ detail.bannedRoleText || detail.session.bannedRoleText || '已封禁' }}</el-tag>
          <span>{{ detail.banReason || '-' }}</span>
        </div>
        <div class="message-list">
          <div v-for="item in detail.messages || []" :key="item.id" class="message-item">
            <div class="message-meta">
              <span>{{ senderLabel(item.senderRole) }}</span>
              <span>{{ item.sentAt || '-' }}</span>
            </div>
            <div class="message-content" :class="{ recalled: Number(item.isRecalled) === 1 }">
              {{ item.content || '-' }}
            </div>
          </div>
        </div>
        <div v-if="isPlatformDirectOrderSession(detail.session)" class="detail-send">
          <div class="detail-send-title">平台发送消息</div>
          <el-input
            v-model="detailSendForm.content"
            type="textarea"
            :rows="4"
            maxlength="300"
            show-word-limit
            placeholder="请输入平台系统消息"
          />
          <div class="detail-send-actions">
            <el-button type="primary" :loading="detailSending" @click="submitDetailSend">发送</el-button>
          </div>
        </div>
      </div>
    </el-drawer>

    <el-dialog title="平台发送系统消息" :visible.sync="sendVisible" width="520px">
      <el-input
        v-model="sendForm.content"
        type="textarea"
        :rows="5"
        maxlength="300"
        show-word-limit
        placeholder="请输入平台系统消息"
      />
      <span slot="footer">
        <el-button @click="sendVisible = false">取消</el-button>
        <el-button type="primary" :loading="sending" @click="submitSend">发送</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import {
  banPrivateMessageSession,
  getPrivateMessageSessionDetail,
  getPrivateMessageSessions,
  sendPrivateMessageSystemNotice,
  unbanPrivateMessageSession
} from '../../../api/platformManage'
import { badgeValue } from '../../../utils/recruitment-badges'

const PLATFORM_OFFICIAL_NAME = 'z7平台官方'

export default {
  name: 'PlatformPrivateMessagesPage',
  data() {
    return {
      query: {
        keyword: '',
        banned: undefined,
        merchantUserId: undefined,
        artistUserId: undefined,
        noticeId: undefined
      },
      noticeTitle: '',
      pollTimer: null,
      autoOpenHandled: false,
      list: [],
      detailVisible: false,
      detail: {},
      sendVisible: false,
      currentSessionId: '',
      sending: false,
      sendForm: {
        content: ''
      },
      detailSendForm: {
        content: ''
      },
      detailSending: false
    }
  },
  created() {
    this.applyRouteQuery()
    this.resetAutoOpenState()
    this.fetchData()
    this.startPolling()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  watch: {
    '$route.query': {
      handler() {
        this.applyRouteQuery()
        this.resetAutoOpenState()
        this.fetchData()
      }
    }
  },
  methods: {
    unreadBadgeValue: badgeValue,
    isPlatformDirectOrderSession(row) {
      return Boolean(row && row.platformDirectOrder)
    },
    hideMerchantBan(row) {
      return this.isPlatformDirectOrderSession(row)
    },
    displayMerchantName(row) {
      if (this.isPlatformDirectOrderSession(row)) {
        return PLATFORM_OFFICIAL_NAME
      }
      const name = row && row.merchantName != null ? String(row.merchantName).trim() : ''
      if (!name || /^\?+$/.test(name)) {
        return '-'
      }
      return name
    },
    applyRouteQuery() {
      const routeQuery = this.$route.query || {}
      this.query.merchantUserId = this.normalizeRouteId(routeQuery.merchantUserId)
      this.query.artistUserId = this.normalizeRouteId(routeQuery.artistUserId)
      this.query.noticeId = this.normalizeRouteId(routeQuery.noticeId)
      this.noticeTitle = routeQuery.noticeTitle ? String(routeQuery.noticeTitle) : ''
      if (routeQuery.keyword !== undefined) {
        this.query.keyword = routeQuery.keyword
      }
    },
    normalizeRouteId(value) {
      if (value === undefined || value === null || value === '') {
        return undefined
      }
      return String(value).trim()
    },
    clearNoticeFilter() {
      this.autoOpenHandled = true
      this.$router.push({ path: '/platform/private-messages' })
    },
    resetAutoOpenState() {
      this.autoOpenHandled = this.$route.query.autoOpen !== '1'
    },
    shouldAutoOpenSession() {
      return !this.autoOpenHandled && this.$route.query.autoOpen === '1' && this.query.noticeId
    },
    clearAutoOpenQuery() {
      if (this.$route.query.autoOpen !== '1') {
        return
      }
      const query = { ...this.$route.query }
      delete query.autoOpen
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    async tryAutoOpenSession(list) {
      if (!this.shouldAutoOpenSession()) {
        return
      }
      this.autoOpenHandled = true
      this.clearAutoOpenQuery()
      if (!Array.isArray(list) || !list.length) {
        return
      }
      const target = list.find(item => Number(item.unreadCount) > 0) || list[0]
      if (target) {
        await this.openDetail(target)
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = window.setInterval(() => {
        this.fetchData({ silent: true })
      }, 15000)
    },
    stopPolling() {
      if (this.pollTimer) {
        window.clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    senderLabel(role) {
      if (Number(role) === 1) return '商家'
      if (Number(role) === 2) return '艺人'
      if (Number(role) === 9) return '平台'
      return '未知'
    },
    bannedRoleText(role) {
      if (Number(role) === 1) return '封禁商家私聊功能'
      if (Number(role) === 2) return '封禁艺人私聊功能'
      return '封禁双方私聊功能'
    },
    async fetchData(options = {}) {
      const list = await getPrivateMessageSessions(this.query).catch(() => [])
      if (options.silent && this.detailVisible && this.detail.session) {
        const currentSessionId = this.detail.session.sessionId
        const refreshed = list.find(item => item.sessionId === currentSessionId)
        if (refreshed) {
          this.detail = await getPrivateMessageSessionDetail(currentSessionId).catch(() => this.detail)
        }
      }
      this.list = list
      if (!options.silent) {
        await this.tryAutoOpenSession(list)
      }
    },
    async openDetail(row) {
      this.detailSendForm.content = ''
      this.detail = await getPrivateMessageSessionDetail(row.sessionId)
      this.detailVisible = true
      await this.fetchData({ silent: true })
    },
    async handleBan(row, bannedRole) {
      const prompt = await MessageBox.prompt(
        `请输入${this.bannedRoleText(bannedRole)}的原因`,
        '封禁私聊',
        {
          confirmButtonText: '确认',
          cancelButtonText: '取消',
          inputValidator: value => (value && value.trim() ? true : '请输入封禁原因')
        }
      ).catch(() => null)
      if (!prompt) return
      await banPrivateMessageSession(row.sessionId, {
        reason: prompt.value.trim(),
        bannedRole
      })
      this.$message.success('封禁成功')
      await this.fetchData()
      if (this.detailVisible && this.detail.session && this.detail.session.sessionId === row.sessionId) {
        this.detail = await getPrivateMessageSessionDetail(row.sessionId)
      }
    },
    async handleUnban(row) {
      await unbanPrivateMessageSession(row.sessionId)
      this.$message.success('解封成功')
      await this.fetchData()
      if (this.detailVisible && this.detail.session && this.detail.session.sessionId === row.sessionId) {
        this.detail = await getPrivateMessageSessionDetail(row.sessionId)
      }
    },
    openSend(row) {
      this.currentSessionId = row.sessionId
      this.sendForm.content = ''
      this.sendVisible = true
    },
    async submitDetailSend() {
      const sessionId = this.detail.session && this.detail.session.sessionId
      if (!sessionId) {
        return
      }
      await this.submitPlatformMessage(sessionId, this.detailSendForm.content, {
        onSuccess: () => {
          this.detailSendForm.content = ''
        },
        loadingKey: 'detailSending'
      })
    },
    async submitSend() {
      await this.submitPlatformMessage(this.currentSessionId, this.sendForm.content, {
        onSuccess: () => {
          this.sendVisible = false
        },
        loadingKey: 'sending'
      })
    },
    async submitPlatformMessage(sessionId, rawContent, options = {}) {
      const content = String(rawContent || '').trim()
      if (!content) {
        this.$message.warning('请输入消息内容')
        return
      }
      const loadingKey = options.loadingKey
      if (loadingKey && this[loadingKey]) {
        return
      }
      if (loadingKey) {
        this[loadingKey] = true
      }
      try {
        await sendPrivateMessageSystemNotice(sessionId, { content })
        this.$message.success('发送成功')
        if (typeof options.onSuccess === 'function') {
          options.onSuccess()
        }
        await this.fetchData()
        if (this.detailVisible && sessionId) {
          this.detail = await getPrivateMessageSessionDetail(sessionId)
        }
      } finally {
        if (loadingKey) {
          this[loadingKey] = false
        }
      }
    }
  }
}
</script>

<style scoped>
.page-title {
  margin-bottom: 0;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.notice-filter-tip {
  color: #606266;
  font-size: 14px;
}

.unread-badge {
  display: inline-block;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #f56c6c;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  font-weight: 600;
  box-sizing: border-box;
}

.detail-wrap {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-weight: 600;
  color: #303133;
}

.detail-ban {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #606266;
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message-item {
  padding: 12px;
  border-radius: 10px;
  background: #f7f8fa;
}

.message-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
  color: #909399;
  font-size: 12px;
}

.message-content {
  color: #303133;
  white-space: pre-wrap;
  word-break: break-word;
}

.message-content.recalled {
  color: #909399;
}

.detail-send {
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}

.detail-send-title {
  margin-bottom: 12px;
  font-weight: 600;
  color: #303133;
}

.detail-send-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
</style>
